import { useEffect, useState, useCallback } from "react";
import bszPlannerService from "../services";
import { useAppContext } from "../../../2_ksbHub/context/appContext";
import bszService from "../../1_BSZFinderApp/services";
import eventService from "../../3_EventManagerApp/services/eventService";

export default function useBszPlannerState() {
  const { currentUser, isATeacher, isAStudent, setBszPlannerLoading } =
    useAppContext();

  /**
   * =========================================================
   * ✅ STATE
   * =========================================================
   */
  const [projects, setProjects] = useState([]);

  const [subjects, setSubjects] = useState([]);
  const [mySubjects, setMySubjects] = useState([]);

  const [classes, setClasses] = useState([]);
  const [myClasses, setMyClasses] = useState([]);

  const [teachers, setTeachers] = useState([]);

  const [schoolyears, setSchoolyears] = useState([]);
  const [selectedSchoolyear, setSelectedSchoolyear] = useState(null);

  /**
   * =========================================================
   * ✅ UI STATE
   * =========================================================
   */
  const [showSubjectsDialog, setShowSubjectsDialog] = useState(false);
  const [showSubjectAdminDialog, setShowSubjectAdminDialog] = useState(false);

  const [showClassesDialog, setShowClassesDialog] = useState(false);
  const [showClassesAdminDialog, setShowClassesAdminDialog] = useState(false);

  const [selectedClass, setSelectedClass] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /**
   * =========================================================
   * ✅ LOADERS
   * =========================================================
   */

  /**
   * =========================================================
   * ✅ load schoolyears
   * =========================================================
   */
  useEffect(() => {
    if (!currentUser) return;

    const loadSchoolyears = async () => {
      const res = await eventService.getSchoolyears();
      setSchoolyears(res.reverse());
      // console.log(res);
      if (res.length > 0) {
        setSelectedSchoolyear(res[0]);
      }
    };

    loadSchoolyears();
  }, [currentUser]);

  /**
   * =========================================================
   * ✅ load projekcts
   * =========================================================
   */

  const loadProjects = useCallback(async (schoolyearId) => {
    if (!schoolyearId) {
      setProjects([]);
      return;
    }

    try {
      const data = await bszPlannerService.getProjects(schoolyearId);

      setProjects(data?.projects || []);
    } catch (error) {
      console.error("Error loading projects:", error);

      setProjects([]);
    }
  }, []);

  const loadClasses = useCallback(async (schoolyearId) => {
    const data = await bszPlannerService.getClasses(schoolyearId);

    const cls = Array.isArray(data?.classes)
      ? data.classes
      : Array.isArray(data)
        ? data
        : [];

    setClasses(cls);
  }, []);

  const loadMyClasses = useCallback(async (schoolyearId) => {
    const data = await bszPlannerService.getMyClasses(schoolyearId);
    setMyClasses(data?.classes || []);
  }, []);

  useEffect(() => {
    if (!selectedSchoolyear?.id) return;

    setProjects([]);
    setClasses();
    setMyClasses([]);

    loadProjects(selectedSchoolyear.id);
    loadClasses(selectedSchoolyear.id);
    loadMyClasses(selectedSchoolyear.id);
  }, [selectedSchoolyear?.id, loadProjects, loadClasses, loadMyClasses]);

  const loadSubjects = useCallback(async () => {
    const data = await bszPlannerService.getSubjects();

    setSubjects(data?.subjects || []);
  }, []);

  const loadMySubjects = useCallback(async () => {
    const data = await bszPlannerService.getMySubjects();

    setMySubjects(data?.subjects || []);
  }, []);

  const loadTeachers = useCallback(async () => {
    const data = await bszPlannerService.getTeachers();

    setTeachers(data?.teachers || []);
  }, []);

  /**
   * =========================================================
   * ✅ PROJECT MUTATIONS
   * =========================================================
   */
  const createProject = async (projectData) => {
    await bszPlannerService.createProject(projectData);

    await loadProjects(selectedSchoolyear?.id);
  };

  const updateProject = async (projectData) => {
    await bszPlannerService.updateProject(projectData);

    await loadProjects(selectedSchoolyear?.id);
  };

  const deleteProject = async (projectId) => {
    await bszPlannerService.deleteProject(projectId);
    setProjects((prev) => prev.filter((project) => project.id !== projectId));
  };

  /**
   * =========================================================
   * ✅ CLASS & SUBJECT MUTATIONS
   * =========================================================
   */
  const saveMyClasses = async (classIds) => {
    await bszService.saveMyClasses(classIds);

    await loadMyClasses(selectedSchoolyear?.id);
  };

  const saveClasses = async (newClasses) => {
    await bszService.saveClasses(newClasses);

    await loadClasses(selectedSchoolyear?.id);
  };

  /**
   * =========================================================
   * ✅ INITIAL LOAD
   * =========================================================
   */
  useEffect(() => {
    if (!currentUser?.id) return;

    const loadData = async () => {
      setBszPlannerLoading(true);

      try {
        await Promise.all([
          loadSubjects(),
          ...(isATeacher ? [loadMySubjects()] : []),
          loadTeachers(),
        ]);
      } finally {
        setBszPlannerLoading(false);
      }
    };

    loadData();
  }, [
    currentUser?.id,
    isATeacher,
    isAStudent,
    loadProjects,
    loadClasses,
    loadSubjects,
    loadTeachers,
    loadMyClasses,
    loadMySubjects,
    setBszPlannerLoading,
  ]);

  /**
   * =========================================================
   * ✅ UI SIDE EFFECTS
   * =========================================================
   */
  // useEffect(() => {
  //   if (isATeacher && mySubjects?.length === 0) {
  //     setShowSubjectsDialog(true);
  //   }
  // }, [isATeacher, mySubjects]);

  // useEffect(() => {
  //   if ((isATeacher || isAStudent) && myClasses?.length === 0) {
  //     setShowClassesDialog(true);
  //   }
  // }, [myClasses, isATeacher, isAStudent]);

  /**
   * =========================================================
   * ✅ EXPORT
   * =========================================================
   */
  return {
    // state
    projects,
    setProjects,

    subjects,
    mySubjects,
    setMySubjects,

    classes,
    myClasses,

    teachers,

    selectedClass,
    setSelectedClass,

    schoolyears,
    setSchoolyears,
    selectedSchoolyear,
    setSelectedSchoolyear,

    // ui
    snackbar,
    setSnackbar,

    showSubjectsDialog,
    setShowSubjectsDialog,

    showSubjectAdminDialog,
    setShowSubjectAdminDialog,

    showClassesDialog,
    setShowClassesDialog,

    showClassesAdminDialog,
    setShowClassesAdminDialog,

    // loaders
    loadProjects,
    loadClasses,
    loadMyClasses,
    loadSubjects,
    loadMySubjects,
    loadTeachers,

    // project mutations
    createProject,
    updateProject,
    deleteProject,

    // class mutations
    saveMyClasses,
    saveClasses,
  };
}
